import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/components/AuthContext";

function AuthLoading() {
	const { t } = useTranslation();
	return (
		<div className="min-h-screen bg-background flex items-center justify-center">
			<p className="text-sm text-muted-foreground">{t("common.loading")}</p>
		</div>
	);
}

function AuthUnavailable({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="min-h-screen bg-background flex items-center justify-center p-4">
			<div className="w-full max-w-sm rounded-lg border border-border bg-card p-5 text-center">
				<p className="text-sm font-medium text-foreground">
					{t("common.authUnavailableTitle")}
				</p>
				<p className="mt-2 text-sm text-muted-foreground">
					{t("common.authUnavailableDescription")}
				</p>
				<button
					type="button"
					onClick={onRetry}
					className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
				>
					{t("common.retry")}
				</button>
			</div>
		</div>
	);
}

export function RequireAuth({ children }: { children: ReactNode }) {
	const { status, refresh } = useAuth();
	const location = useLocation();
	if (status === "checking") return <AuthLoading />;
	if (status === "unavailable") {
		return <AuthUnavailable onRetry={() => void refresh()} />;
	}
	if (status !== "authenticated") {
		const returnTo = `${location.pathname}${location.search}${location.hash}`;
		return <Navigate to="/login" replace state={{ returnTo }} />;
	}
	return children;
}

export function RequireGuest({ children }: { children: ReactNode }) {
	const { status, refresh } = useAuth();
	if (status === "checking") return <AuthLoading />;
	if (status === "unavailable") {
		return <AuthUnavailable onRetry={() => void refresh()} />;
	}
	return status === "guest" ? children : <Navigate to="/dashboard" replace />;
}
